import React, { useContext, useState, useRef } from 'react';
import { AuthContext } from '../App';
import FacultyLayout from './FacultyLayout';
import { User, Mail, Building, Save, Camera, Loader2 } from 'lucide-react';
import { profileAPI, resolveServerUrl } from '../../services/api';

export default function FacultyProfile() {
  const { user, setUser } = useContext(AuthContext);
  const [isEditing, setIsEditing] = useState(false);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    department: user?.department || '',
    bio: user?.bio || 'Passionate about creative writing and digital arts. Faculty member at BISU with 10+ years of experience.',
  });

  // Handle profile picture upload
  const handlePictureClick = () => {
    fileInputRef.current?.click();
  };

  const handlePictureChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB');
      return;
    }

    try {
      setUploadingPicture(true);
      const response = await profileAPI.uploadProfilePicture(file);
      console.log('Upload response:', response);
      
      if (response.user && setUser) {
        // Update the user context with new profile picture
        setUser(response.user);
        localStorage.setItem('imdc_user', JSON.stringify(response.user));
        alert('Profile picture updated successfully!');
      } else if (response.profile_picture) {
        // Backend might return just the profile_picture path
        const updatedUser = { ...user, profile_picture: response.profile_picture };
        setUser(updatedUser as any);
        localStorage.setItem('imdc_user', JSON.stringify(updatedUser));
        alert('Profile picture updated successfully!');
      }
    } catch (error: any) {
      console.error('Error uploading profile picture:', error);
      const errorMessage = error?.message || 'Unknown error occurred';
      alert(`Failed to upload profile picture: ${errorMessage}`);
    } finally {
      setUploadingPicture(false);
    }
  };

  // Get profile picture URL
  const getProfilePictureUrl = () => {
    if (user?.profile_picture) {
      return resolveServerUrl(user.profile_picture);
    }
    return null;
  };

  const profilePictureUrl = getProfilePictureUrl();

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Mock save
    setIsEditing(false);
  };

  const departments = [
    'College of Arts and Sciences',
    'College of Engineering',
    'College of Education',
    'College of Business Administration',
    'College of Agriculture',
    'College of Technology',
    'College of Nursing',
    'Graduate School',
  ];

  return (
    <FacultyLayout>
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">My Profile</h1>
          <p className="text-gray-600">Manage your account information and settings</p>
        </div>

        {/* Profile Card */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {/* Header Banner */}
          <div className="h-32 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600" />

          <div className="px-8 pb-8">
            {/* Profile Picture */}
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between -mt-16 mb-6">
              <div className="flex items-end space-x-4">
                {/* Clickable Profile Picture */}
                <div className="relative">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handlePictureChange}
                    accept="image/*"
                    className="hidden"
                  />
                  <div className="w-32 h-32 rounded-2xl border-4 border-white shadow-lg overflow-hidden">
                    {profilePictureUrl ? (
                      <img
                        src={profilePictureUrl}
                        alt={user?.name || 'Profile'}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                        <span className="text-5xl text-white font-bold">
                          {user?.name?.charAt(0) || 'F'}
                        </span>
                      </div>
                    )}
                  </div>
                  {/* Change Photo Button */}
                  <div className="flex gap-1 mt-2">
                    {profilePictureUrl && (
                      <button
                        type="button"
                        onClick={() => window.open(profilePictureUrl, '_blank')}
                        className="flex-1 text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                      >
                        View
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={handlePictureClick}
                      disabled={uploadingPicture}
                      className="flex-1 text-xs px-2 py-1 bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-1"
                    >
                      {uploadingPicture ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Camera className="w-3 h-3" />
                      )}
                      {uploadingPicture ? 'Uploading...' : 'Change'}
                    </button>
                  </div>
                </div>
                <div className="pb-2">
                  <h2 className="text-2xl font-bold text-gray-900">{user?.name}</h2>
                  <p className="text-gray-600">{user?.email}</p>
                </div>
              </div>
              {!isEditing && (
                <button
                  onClick={() => setIsEditing(true)}
                  className="mt-4 sm:mt-0 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
                >
                  Edit Profile
                </button>
              )}
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block font-medium text-gray-900 mb-2">
                    Full Name
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleChange}
                      disabled={!isEditing}
                      className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-600"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block font-medium text-gray-900 mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      disabled={!isEditing}
                      className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-600"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="department" className="block font-medium text-gray-900 mb-2">
                  Department/College
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Building className="h-5 w-5 text-gray-400" />
                  </div>
                  <select
                    id="department"
                    name="department"
                    value={formData.department}
                    onChange={handleChange}
                    disabled={!isEditing}
                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-600 appearance-none"
                  >
                    {departments.map((dept) => (
                      <option key={dept} value={dept}>
                        {dept}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {isEditing && (
                <div className="flex items-center justify-end space-x-4 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setIsEditing(false)}
                    className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="inline-flex items-center px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-semibold"
                  >
                    <Save className="w-5 h-5 mr-2" />
                    Save Changes
                  </button>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </FacultyLayout>
  );
}
